import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { FileText, Building2, TrendingUp, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

// Country names by language
const COUNTRY_NAMES: Record<string, Record<string, string>> = {
  IT: { it: 'Italia', en: 'Italy', fr: 'Italie', de: 'Italien', es: 'Italia' },
  FR: { it: 'Francia', en: 'France', fr: 'France', de: 'Frankreich', es: 'Francia' },
  DE: { it: 'Germania', en: 'Germany', fr: 'Allemagne', de: 'Deutschland', es: 'Alemania' },
  ES: { it: 'Spagna', en: 'Spain', fr: 'Espagne', de: 'Spanien', es: 'España' },
  PT: { it: 'Portogallo', en: 'Portugal', fr: 'Portugal', de: 'Portugal', es: 'Portugal' },
  NL: { it: 'Paesi Bassi', en: 'Netherlands', fr: 'Pays-Bas', de: 'Niederlande', es: 'Países Bajos' },
  BE: { it: 'Belgio', en: 'Belgium', fr: 'Belgique', de: 'Belgien', es: 'Bélgica' },
  AT: { it: 'Austria', en: 'Austria', fr: 'Autriche', de: 'Österreich', es: 'Austria' },
  PL: { it: 'Polonia', en: 'Poland', fr: 'Pologne', de: 'Polen', es: 'Polonia' },
  RO: { it: 'Romania', en: 'Romania', fr: 'Roumanie', de: 'Rumänien', es: 'Rumanía' },
  GR: { it: 'Grecia', en: 'Greece', fr: 'Grèce', de: 'Griechenland', es: 'Grecia' },
  CZ: { it: 'Repubblica Ceca', en: 'Czech Republic', fr: 'République tchèque', de: 'Tschechien', es: 'República Checa' },
  HU: { it: 'Ungheria', en: 'Hungary', fr: 'Hongrie', de: 'Ungarn', es: 'Hungría' },
  SE: { it: 'Svezia', en: 'Sweden', fr: 'Suède', de: 'Schweden', es: 'Suecia' },
  DK: { it: 'Danimarca', en: 'Denmark', fr: 'Danemark', de: 'Dänemark', es: 'Dinamarca' },
  FI: { it: 'Finlandia', en: 'Finland', fr: 'Finlande', de: 'Finnland', es: 'Finlandia' },
  IE: { it: 'Irlanda', en: 'Ireland', fr: 'Irlande', de: 'Irland', es: 'Irlanda' },
  SK: { it: 'Slovacchia', en: 'Slovakia', fr: 'Slovaquie', de: 'Slowakei', es: 'Eslovaquia' },
  BG: { it: 'Bulgaria', en: 'Bulgaria', fr: 'Bulgarie', de: 'Bulgarien', es: 'Bulgaria' },
  HR: { it: 'Croazia', en: 'Croatia', fr: 'Croatie', de: 'Kroatien', es: 'Croacia' },
  SI: { it: 'Slovenia', en: 'Slovenia', fr: 'Slovénie', de: 'Slowenien', es: 'Eslovenia' },
  LT: { it: 'Lituania', en: 'Lithuania', fr: 'Lituanie', de: 'Litauen', es: 'Lituania' },
  LV: { it: 'Lettonia', en: 'Latvia', fr: 'Lettonie', de: 'Lettland', es: 'Letonia' },
  EE: { it: 'Estonia', en: 'Estonia', fr: 'Estonie', de: 'Estland', es: 'Estonia' },
  CY: { it: 'Cipro', en: 'Cyprus', fr: 'Chypre', de: 'Zypern', es: 'Chipre' },
  LU: { it: 'Lussemburgo', en: 'Luxembourg', fr: 'Luxembourg', de: 'Luxemburg', es: 'Luxemburgo' },
  MT: { it: 'Malta', en: 'Malta', fr: 'Malte', de: 'Malta', es: 'Malta' },
};

// Simulated tender counts per country (annual estimates based on TED data)
const COUNTRY_TENDER_COUNTS: Record<string, number> = {
  IT: 42500,
  FR: 68000,
  DE: 95000,
  ES: 38000,
  PT: 12000,
  NL: 18500,
  BE: 15000,
  AT: 9500,
  PL: 52000,
  RO: 28000,
  GR: 8500,
  CZ: 22000,
  HU: 18000,
  SE: 14000,
  DK: 8000,
  FI: 9000,
  IE: 6500,
  SK: 11000,
  BG: 14500,
  HR: 7500,
  SI: 5000,
  LT: 8000,
  LV: 6000,
  EE: 4500,
  CY: 2000,
  LU: 1500,
  MT: 1200,
};

// Average tender values per country (in millions EUR)
const COUNTRY_AVG_VALUES: Record<string, number> = {
  IT: 2.8,
  FR: 3.2,
  DE: 4.5,
  ES: 2.1,
  PT: 1.8,
  NL: 3.8,
  BE: 2.9,
  AT: 3.1,
  PL: 1.5,
  RO: 1.2,
  GR: 1.9,
  CZ: 1.7,
  HU: 1.4,
  SE: 3.5,
  DK: 3.3,
  FI: 2.8,
  IE: 2.5,
  SK: 1.3,
  BG: 0.9,
  HR: 1.1,
  SI: 1.6,
  LT: 1.0,
  LV: 0.8,
  EE: 1.2,
  CY: 1.5,
  LU: 2.2,
  MT: 1.8,
};

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}

function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '', decimals = 0 }: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  const countRef = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Easing function for smooth animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            setCount(Math.floor(easeOutQuart * end * Math.pow(10, decimals)) / Math.pow(10, decimals));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated, decimals]);

  const formatNumber = (num: number) => {
    if (decimals > 0) {
      return num.toFixed(decimals);
    }
    return num.toLocaleString();
  };

  return (
    <span ref={countRef}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
}

interface CountryStatsProps {
  detectedCountry: string;
}

export default function CountryStats({ detectedCountry }: CountryStatsProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'it';
  
  const countryName = COUNTRY_NAMES[detectedCountry]?.[currentLang] || COUNTRY_NAMES['IT'][currentLang] || 'Italia';
  const tenderCount = COUNTRY_TENDER_COUNTS[detectedCountry] || COUNTRY_TENDER_COUNTS['IT'];
  const avgValue = COUNTRY_AVG_VALUES[detectedCountry] || COUNTRY_AVG_VALUES['IT'];
  const monthlyTenders = Math.round(tenderCount / 12);
  const totalBuyers = Math.round(tenderCount * 0.15); // Estimated unique buyers

  const stats = [
    {
      icon: FileText,
      value: tenderCount,
      suffix: '+',
      label: t('landing.countryStats.annualTenders'),
    },
    {
      icon: Calendar,
      value: monthlyTenders,
      suffix: '',
      label: t('landing.countryStats.monthlyTenders'),
    },
    {
      icon: TrendingUp,
      value: avgValue,
      prefix: '€',
      suffix: 'M',
      decimals: 1,
      label: t('landing.countryStats.avgValue'),
    },
    {
      icon: Building2,
      value: totalBuyers,
      suffix: '+',
      label: t('landing.countryStats.buyers'),
    },
  ];

  return (
    <section className="py-3 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
      <div className="container mx-auto px-4 relative z-10">
        <h2 className="text-lg md:text-xl font-bold text-foreground mb-1 text-center">
          {t('landing.countryStats.title')}
        </h2>
        <p className="text-xs text-muted-foreground mb-2 text-center">
          {t('landing.countryStats.subtitle')}
        </p>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {stats.map(({ icon: Icon, value, suffix, prefix, decimals, label }, index) => (
            <Card 
              key={index}
              className="border-0 shadow-sm bg-card/80 backdrop-blur-sm group hover:shadow-md transition-all duration-300 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-3 text-center">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 text-primary mb-1.5 group-hover:scale-110 transition-transform">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-xl md:text-2xl font-bold text-foreground">
                  <AnimatedCounter 
                    end={value} 
                    suffix={suffix} 
                    prefix={prefix} 
                    decimals={decimals}
                    duration={2000}
                  />
                </div>
                <div className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
